import { useTranslation } from "react-i18next";
import { Button } from "@wondermaps/ui";
import { StarRow } from "../../shared/StarRow";
import { POI_RATING_TAGS } from "../../../lib/poi-rating-tags";
import type { usePoiRating } from "../../../hooks/visitor/usePoiRating";

type POIRatingProps = ReturnType<typeof usePoiRating> & {
  onRequestLocation?: () => void;
};

export function POIRatingSection({
  phase,
  canRate,
  gpsPrompt,
  rating,
  setRating,
  comment,
  setComment,
  selectedTags,
  toggleTag,
  openForm,
  submit,
  onRequestLocation,
}: POIRatingProps) {
  const { t } = useTranslation("pois");
  if (phase === "thanks") {
    return (
      <p className="text-sm text-muted-foreground text-center py-2">
        {t("thanksFeedback")}
      </p>
    );
  }

  if (phase === "form") {
    return (
      <div className="space-y-3 rounded-lg border p-3">
        <StarRow rating={rating} onRate={setRating} size="sm" />

        {/* Tag chips */}
        <div className="flex flex-wrap gap-1.5">
          {POI_RATING_TAGS.map((tag) => (
            <button
              key={tag.key}
              type="button"
              onClick={() => toggleTag(tag.key)}
              className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                selectedTags.has(tag.key)
                  ? tag.color
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {t(`ratingTags.${tag.key}`)}
            </button>
          ))}
        </div>

        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t("anyThoughts")}
          maxLength={500}
          rows={2}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
        />

        <Button
          size="sm"
          className="w-full"
          onClick={submit}
          disabled={rating === null}
        >
          {t("common:submit")}
        </Button>
      </div>
    );
  }

  // idle — card with inline stars
  if (!canRate) return null;

  const handleStarClick = (n: number) => {
    if (onRequestLocation) {
      // GPS not active — request location first
      onRequestLocation();
    } else {
      setRating(n);
      openForm();
    }
  };

  return (
    <div className="rounded-2xl border shadow-sm p-3 flex flex-col items-center gap-3">
      <span className="text-base font-medium">{t("whatDidYouThink")}</span>
      <StarRow rating={null} onRate={handleStarClick} size="lg" variant="outline" />
      {gpsPrompt === "prompt" && (
        <p className="text-xs text-muted-foreground">
          {t("gpsPromptEnable")}
        </p>
      )}
      {gpsPrompt === "denied" && (
        <p className="text-xs text-muted-foreground">
          {t("gpsPromptDenied")}
        </p>
      )}
    </div>
  );
}
